import type {
  DevFailedIngestionDto,
  DevOverviewDto,
  DevPreferredDemandForecastDto,
} from '@/api';
import { themeColors } from '@/theme/colors';
import { formatDateTimeDisplay } from '@/utils/date-utils';
import { Tag } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import type { ReactNode } from 'react';
import {
  getIngestionStatusDisplay,
  getPriceEventsStatusDisplay,
} from '../dev-dashboard-helpers';
import { DailyCountLineChart } from './daily-count-line-chart';

type Props = {
  overview: DevOverviewDto;
};

const OverviewCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Flex
    as="section"
    aria-label={title}
    flexDirection="column"
    gap={3}
    padding={4}
    borderRadius="8px"
    backgroundColor={themeColors.surface.background.base.default}
  >
    <Text fontSize="md" fontWeight="bold">
      {title}
    </Text>
    {children}
  </Flex>
);

const Indicator = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Flex flexDirection="column" gap={1}>
    <Text fontSize="sm" color={themeColors.content.subtle}>
      {label}
    </Text>
    {children}
  </Flex>
);

const formatOptional = (value: Date | null | undefined) =>
  value ? formatDateTimeDisplay(value) : '-';

const ForecastRow = ({
  forecast,
}: {
  forecast: DevPreferredDemandForecastDto;
}) => (
  <Indicator label={String(forecast.year)}>
    <Text fontSize="sm">
      Quantity: <strong>{forecast.quantityForecastVersion ?? '-'}</strong>
    </Text>
    <Text fontSize="sm">
      Discount: <strong>{forecast.discountForecastVersion ?? '-'}</strong>
    </Text>
  </Indicator>
);

export const OverviewPanel = ({ overview }: Props) => {
  const ingestion = getIngestionStatusDisplay(overview.etl.lastSlotStatus);
  const priceEvents = getPriceEventsStatusDisplay(overview.priceEvents);
  // The backend sends null when no ingestion failed; the generated type does not show that.
  const lastFailure = overview.etl.lastFailure as DevFailedIngestionDto | null;

  return (
    <Flex flexDirection="column" gap={3} paddingBottom={3}>
      <OverviewCard title="ETL">
        <Flex gap={10} flexWrap="wrap">
          <Indicator
            label={`Last ingestion (slot ${formatDateTimeDisplay(overview.etl.lastSlotAt)})`}
          >
            <Flex>
              <Tag variant={ingestion.variant} aria-label={ingestion.label}>
                {ingestion.label}
              </Tag>
            </Flex>
          </Indicator>
          <Indicator label="Last failed ingestion">
            {lastFailure ? (
              <Text fontSize="sm">
                {formatOptional(lastFailure.failedAt ?? lastFailure.startedAt)}
                {' · '}
                <strong>{lastFailure.failedStep ?? 'Unknown step'}</strong>
              </Text>
            ) : (
              <Text fontSize="sm">No failed ingestion.</Text>
            )}
          </Indicator>
        </Flex>
      </OverviewCard>

      <OverviewCard title="Price changes">
        <Indicator label="Price events status">
          <Flex gap={2} alignItems="center" flexWrap="wrap">
            <Tag variant={priceEvents.variant} aria-label={priceEvents.label}>
              {priceEvents.label}
            </Tag>
            <Text fontSize="sm">{priceEvents.description}</Text>
          </Flex>
        </Indicator>
        <Indicator label="Prices changed per day, last 7 days (UTC)">
          <DailyCountLineChart
            points={overview.priceChanges}
            ariaLabel="Prices changed per day, last 7 days"
          />
        </Indicator>
      </OverviewCard>

      <OverviewCard title="Preferred demand forecasts">
        {overview.preferredDemandForecasts.length === 0 ? (
          <Text fontSize="sm">
            No current preferred forecasts for this and next year.
          </Text>
        ) : (
          <Flex gap={10} flexWrap="wrap">
            {overview.preferredDemandForecasts.map((forecast) => (
              <ForecastRow key={forecast.year} forecast={forecast} />
            ))}
          </Flex>
        )}
      </OverviewCard>
    </Flex>
  );
};
